<template>
  <div class="py-3 h-100">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <p class="mb-0" :class="{ 'text-danger': invalidFields.length }">
        JKR Work Order
      </p>
    </div>

    <!-- Submitted work orders: read-only, compact card grid -->
    <div class="row g-3 mb-3" v-if="jkrWos.length">
      <div
        class="col-12 col-md-6 col-xl-4"
        v-for="(wo, index) in jkrWos"
        :key="wo._id"
      >
        <div class="card bg-dark border-secondary text-light h-100">
          <div class="card-body d-flex align-items-center py-3">
            <div class="flex-shrink-0 me-3 text-center">
              <button
                class="btn btn-dark border border-secondary px-3 py-2 lh-sm"
                :disabled="!wo.attachment"
                @click="wo.attachment && (currentPdf = wo.attachment.url)"
              >
                <i class="fa-solid fa-file-pdf fa-2x d-block mb-1" />
                <span class="small">{{
                  wo.attachment ? "View" : "No file"
                }}</span>
              </button>
              <!-- Wrong-file recovery: replaceable until Site Diary completes -->
              <button
                class="btn btn-link btn-sm p-0 mt-1 d-block mx-auto small"
                v-if="!isFrozen"
                @click="replacePdf(wo)"
              >
                Replace PDF
              </button>
            </div>
            <div class="text-start overflow-hidden">
              <div class="fw-bold text-nowrap">
                Work Order {{ index + 1 }}
                <span class="badge bg-secondary ms-1" v-if="index > 0">
                  Extension
                </span>
              </div>
              <div
                class="small mt-1"
                v-if="wo.start_date && wo.end_date && !dateDrafts[wo._id]"
              >
                <i class="fa-regular fa-calendar me-1" />
                {{ Helper.formatDate(wo.start_date) }} —
                {{ Helper.formatDate(wo.end_date) }}
                <!-- Dates stay correctable until Site Diary is completed -->
                <button
                  class="btn btn-link btn-sm p-0 ms-2 align-baseline"
                  v-if="!isFrozen"
                  title="Edit working period"
                  @click="editDates(wo)"
                >
                  <i class="fa-regular fa-pen-to-square" />
                </button>
              </div>
              <!-- Working period is user-filled: entered here for a work
                   order that has none, or corrected via the pencil. -->
              <div class="small mt-1" v-else-if="dateDrafts[wo._id]">
                <div class="d-flex align-items-center flex-wrap">
                  <v-date-picker
                    v-model="dateDrafts[wo._id].start_date"
                    :model-config="dateModelConfig"
                    :masks="dateMasks"
                    :disabled-dates="otherWoRanges(wo._id)"
                    is-dark
                  >
                    <template v-slot="{ inputValue, inputEvents }">
                      <input
                        class="form-control form-control-sm w-auto"
                        placeholder="Start"
                        readonly
                        :value="inputValue"
                        v-on="inputEvents"
                      />
                    </template>
                  </v-date-picker>
                  <span class="mx-1">—</span>
                  <v-date-picker
                    v-model="dateDrafts[wo._id].end_date"
                    :model-config="dateModelConfig"
                    :masks="dateMasks"
                    :disabled-dates="otherWoRanges(wo._id)"
                    :min-date="dateDrafts[wo._id].start_date || null"
                    is-dark
                  >
                    <template v-slot="{ inputValue, inputEvents }">
                      <input
                        class="form-control form-control-sm w-auto"
                        placeholder="End"
                        readonly
                        :value="inputValue"
                        v-on="inputEvents"
                      />
                    </template>
                  </v-date-picker>
                </div>
                <button
                  class="btn btn-sm btn-success mt-2"
                  :disabled="!draftValid(wo._id)"
                  @click="saveDates(wo)"
                >
                  Save dates
                </button>
                <button
                  class="btn btn-sm btn-secondary mt-2 ms-1"
                  v-if="wo.start_date && wo.end_date"
                  @click="cancelEditDates(wo)"
                >
                  Cancel
                </button>
              </div>
              <!-- Frozen case with no period on record: the editor above is
                   withheld after Site Diary completes, so say so rather
                   than leaving the line blank. -->
              <div class="small mt-1 text-white-50" v-else>
                <i class="fa-regular fa-calendar me-1" />
                No working period recorded
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No work order yet: the FIRST one is created here. "Extension of Time"
         in Site Diary extends an existing period, so it cannot be the only
         way in — before INFS-1423 it was, which left this step a dead end.
         Hidden once frozen: a completed Site Diary makes every work-order
         record read-only. -->
    <div
      class="p-4 text-center text-secondary"
      v-if="!jkrWos.length && !showForm"
    >
      <p class="fst-italic mb-3">No JKR work order submitted yet.</p>
      <button class="btn btn-success" v-if="!isFrozen" @click="startExtend()">
        <i class="fa-solid fa-plus me-2" />
        Add Work Order
      </button>
    </div>

    <!-- New work order: the first one from the button above, an extension
         from Site Diary's "Extension of Time". -->
    <div class="card bg-dark border-success text-light" v-if="showForm">
      <div class="card-header fw-bold">
        {{ jkrWos.length ? `Work Order ${jkrWos.length + 1} — Extension` : "New Work Order" }}
      </div>
      <div class="card-body">
        <div class="row mb-3">
          <div class="col-6">
            <label class="form-label small">Start date</label>
            <!-- Days already inside another work order's period are disabled
                 directly in the picker; validation stays as the backstop. -->
            <v-date-picker
              v-model="models.start_date"
              :model-config="dateModelConfig"
              :masks="dateMasks"
              :disabled-dates="otherWoRanges(null)"
              is-dark
            >
              <template v-slot="{ inputValue, inputEvents }">
                <input
                  class="form-control"
                  :class="{ 'is-invalid': invalidFields.includes('start_date') }"
                  placeholder="Select start date"
                  readonly
                  :value="inputValue"
                  v-on="inputEvents"
                />
              </template>
            </v-date-picker>
            <div
              class="small text-danger mt-1"
              v-if="invalidFields.includes('start_date')"
            >
              Start date is required
            </div>
          </div>
          <div class="col-6">
            <label class="form-label small">End date</label>
            <v-date-picker
              v-model="models.end_date"
              :model-config="dateModelConfig"
              :masks="dateMasks"
              :disabled-dates="otherWoRanges(null)"
              :min-date="models.start_date || null"
              is-dark
            >
              <template v-slot="{ inputValue, inputEvents }">
                <input
                  class="form-control"
                  :class="{ 'is-invalid': invalidFields.includes('end_date') }"
                  placeholder="Select end date"
                  readonly
                  :value="inputValue"
                  v-on="inputEvents"
                />
              </template>
            </v-date-picker>
            <div
              class="small text-danger mt-1"
              v-if="invalidFields.includes('end_date')"
            >
              End date is required and cannot be before the start date
            </div>
          </div>
        </div>

        <div class="image-grid image-grid-placeholder" v-if="!filePreview">
          <button class="btn h-100 w-100 fw-bold lh-sm" @click="addImage()">
            <i class="fa-solid fa-plus fa-2x mb-2 d-block" />
            Add JKR Work Order File
          </button>
        </div>
        <div v-else>
          <div class="image-grid image-grid-placeholder border-0 bg-dark">
            <button
              class="btn btn-dark h-100 w-100 lh-sm"
              @click="currentPdf = filePreview"
            >
              <i class="fa-solid fa-file-pdf fa-2x mb-2 d-block" />
              Click to preview
            </button>
          </div>
          <button
            class="btn btn-sm btn-outline-light mt-2"
            @click="addImage()"
          >
            <i class="fa-solid fa-arrows-rotate me-1" />
            Change file
          </button>
        </div>

        <div class="w-100 py-3 d-flex justify-content-end">
          <button
            class="btn btn-success mx-1"
            :disabled="!fileInput"
            @click="submitWO"
          >
            Submit
          </button>
          <!-- Always available while the form is open. It used to require a
               picked file or an existing work order, so creating a FIRST work
               order and changing your mind left no way out of the form. -->
          <button class="btn btn-secondary mx-1" @click="cancelAttachment">
            Cancel
          </button>
        </div>
      </div>
    </div>

    <pdf-viewer
      :url="currentPdf"
      v-if="currentPdf"
      @close="currentPdf = null"
    />
  </div>
</template>

<script>
import Swal from "sweetalert2";
import PdfViewer from "@/components/PdfViewer";

export default {
  props: ["componentId", "nodStatus", "nodId", "caseId", "extendSignal"],
  components: { PdfViewer },
  data() {
    return {
      jkrWos: [],
      fileInput: null,
      filePreview: null,
      invalidFields: [],
      currentPdf: null,
      extending: false,
      models: { start_date: null, end_date: null },
      dateDrafts: {},
      // Keeps v-date-picker v-model values as YYYY-MM-DD strings, matching
      // what the API stores and what the validation compares.
      dateModelConfig: { type: "string", mask: "YYYY-MM-DD" },
      // What the pickers' text inputs show. Without it v-calendar follows the
      // browser locale, which rendered 01/15/2024; JKR reads dates DD/MM/YYYY.
      dateMasks: { input: "DD/MM/YYYY" },
    };
  },
  computed: {
    // Read-only by default. The upload form (first work order or an
    // extension) opens ONLY via Site Diary's "Extend Work Order" button.
    showForm() {
      return this.extending;
    },
    // Site Diary completion freezes every work-order record: diary entries
    // are only accepted inside a working period, so editing a period after
    // completion would retroactively invalidate diaries already accepted.
    // Single source of truth — the dateless-card editor below is a third
    // edit path that has to honour it too, not just the pencil and Replace PDF.
    isFrozen() {
      return !!this.nodStatus && this.nodStatus.site_diary === true;
    },
  },
  watch: {
    // Site Diary's "Extend Work Order" button lands here via CaseDetails.
    extendSignal(val) {
      if (val) this.consumeExtend();
    },
  },
  methods: {
    // Open the form once per button press: the parent resets the signal on
    // "extend-consumed" so revisiting this tab later does not reopen it.
    consumeExtend() {
      this.startExtend();
      this.$emit("extend-consumed");
    },
    startExtend() {
      this.extending = true;
      this.models = { start_date: null, end_date: null };
    },
    cancelAttachment() {
      this.fileInput = null;
      if (this.filePreview) URL.revokeObjectURL(this.filePreview);
      this.filePreview = null;
      this.extending = false;
      this.invalidFields = [];
      this.models = { start_date: null, end_date: null };
    },
    // Existing working periods, as Date ranges for the pickers'
    // disabled-dates — days already claimed by another work order cannot be
    // picked at all.
    otherWoRanges(excludeId) {
      const ranges = this.jkrWos
        .filter(
          (wo) =>
            wo.start_date && wo.end_date && (!excludeId || wo._id != excludeId)
        )
        .map((wo) => ({
          start: new Date(wo.start_date),
          end: new Date(wo.end_date),
        }));

      return ranges.length ? ranges : null;
    },
    // Working periods of one case must not overlap (backend enforces the
    // same rule). Day-precision comparison on YYYY-MM-DD strings.
    overlapsExisting(start, end, excludeId) {
      return this.jkrWos.find((wo) => {
        if (excludeId && wo._id == excludeId) return false;
        if (!wo.start_date || !wo.end_date) return false;

        return (
          String(start).slice(0, 10) <= String(wo.end_date).slice(0, 10) &&
          String(end).slice(0, 10) >= String(wo.start_date).slice(0, 10)
        );
      });
    },
    validate() {
      this.invalidFields = [];

      if (!this.models.start_date) this.invalidFields.push("start_date");
      if (
        !this.models.end_date ||
        (this.models.start_date &&
          this.models.end_date < this.models.start_date)
      ) {
        this.invalidFields.push("end_date");
      }

      if (!this.invalidFields.length) {
        const clash = this.overlapsExisting(
          this.models.start_date,
          this.models.end_date,
          null
        );

        if (clash) {
          this.invalidFields.push("start_date", "end_date");

          Swal.fire(
            "Dates overlap",
            `This period overlaps Work Order ${this.jkrWos.indexOf(clash) + 1} (${this.Helper.formatDate(clash.start_date)} — ${this.Helper.formatDate(clash.end_date)}). Work order periods cannot overlap.`,
            "warning"
          );
        }
      }

      return !this.invalidFields.length;
    },
    submitWO() {
      if (!this.validate()) return;

      Swal.fire({
        icon: "question",
        title: "<h5 class='fw-bold'>Submit JKR work order data?</h5>",
        html: "<p class='mb-0'>This action is not reversible.<br />Data cannot be changed after it is submitted.</p>",
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: "Confirm",
      }).then(async (result) => {
        if (result.isConfirmed) {
          this.Helper.toggleLoadingSpinner(true);

          let _nodId = this.nodId;

          if (!_nodId) {
            const [call, error] = await this.Helper.handlePromise(
              this.API.post("nod-cases", {
                case: this.caseId,
              })
            );

            if (error) {
              console.error(error);

              this.triggerAlert("error", error);

              this.Helper.toggleLoadingSpinner(false);

              return;
            } else {
              _nodId = call.data._id;
            }
          }

          const body = {
            datetime: new Date().toISOString(),
            start_date: this.models.start_date,
            end_date: this.models.end_date,
            nod_case: _nodId,
          };

          const [call, error] = await this.Helper.handlePromise(
            this.API.post("nod-jkr-aks", body)
          );

          if (error) {
            console.error(error);

            this.triggerAlert("error", error);
          } else {
            try {
              await this.uploadFileInput(call.data._id);
            } catch (uploadError) {
              console.error(uploadError);

              this.Helper.toggleLoadingSpinner(false);

              // The record exists now, so leaving the form open would invite
              // a resubmit that the no-overlap check is guaranteed to reject
              // against the record just created. Close it and send the user
              // to the one path that can still attach the file.
              this.cancelAttachment();

              await this.getJkrWos();

              return this.triggerAlert(
                "error",
                "Work order saved but the PDF upload failed — use Replace PDF on the new card to attach it"
              );
            }

            const componentStatus = this.nodStatus;

            componentStatus.jkr_wo = true;

            await this.API.put(`nod-cases/${_nodId}`, {
              componentStatus: componentStatus,
            });

            this.cancelAttachment();

            await this.getJkrWos();

            this.triggerAlert(
              "success",
              "JKR AK uploaded",
              "componentIsCompleted",
              call.data._id
            );
          }

          this.Helper.toggleLoadingSpinner(false);
        }
      });
    },
    async uploadFileInput(refId, file) {
      const theFile = file || this.fileInput;

      return new Promise(async (resolve, reject) => {
        let fileModels = {
          ref: "nod-jkr-ak",
          refId: refId,
          field: "attachment",
          files: "",
        };

        const formData = new FormData();
        Object.keys(fileModels).forEach((key) => {
          if (key != "files") {
            formData.append(key, fileModels[key]);
          } else {
            formData.append("files", theFile, theFile.name);
          }
        });

        const [call, error] = await this.Helper.handlePromise(
          this.API.postForm("upload", formData)
        );

        if (error) {
          return reject(error);
        } else {
          return resolve("OK");
        }
      });
    },
    replacePdf(wo) {
      const input = document.createElement("input");

      input.type = "file";
      input.accept = "application/pdf";

      input.onchange = (e) => {
        const file = e.target.files && e.target.files[0];

        if (!file) return;

        Swal.fire({
          icon: "question",
          title: "<h5 class='fw-bold'>Replace this work order PDF?</h5>",
          html: `<p class='mb-0'>The current document will be replaced by<br /><strong>${file.name}</strong></p>`,
          showCancelButton: true,
          reverseButtons: true,
          confirmButtonText: "Replace",
        }).then(async (result) => {
          if (!result.isConfirmed) return;

          this.Helper.toggleLoadingSpinner(true);

          // Remove the old file first so it does not orphan in the media
          // library. A failed delete (e.g. legacy provider) must not block
          // the replacement — the new upload overwrites the relation anyway.
          if (wo.attachment) {
            const oldId = wo.attachment._id || wo.attachment.id;
            const [, delError] = await this.Helper.handlePromise(
              this.API.del(`upload/files/${oldId}`)
            );
            if (delError) console.error(delError);
          }

          try {
            await this.uploadFileInput(wo._id, file);
          } catch (uploadError) {
            console.error(uploadError);

            this.Helper.toggleLoadingSpinner(false);

            await this.getJkrWos();

            return this.triggerAlert("error", "PDF replacement failed");
          }

          await this.getJkrWos();

          this.Helper.toggleLoadingSpinner(false);

          this.Helper.successToast("Work order PDF replaced");
        });
      };

      input.click();
    },
    triggerAlert(icon, text, callback, callbackData) {
      Swal.fire({
        icon: icon,
        title: `<h5>${text}</h5>`,
      }).then(() => {
        if (callback) {
          this.$emit(callback, callbackData);
        }
      });
    },
    addImage() {
      const input = document.createElement("input");

      input.type = "file";

      input.accept = "application/pdf";

      input.onchange = this.fileChanged;

      input.click();
    },
    fileChanged(e) {
      const files = e.target.files;
      if (!files || !files.length) return;

      Object.values(files).forEach((file) => {
        if (this.filePreview) URL.revokeObjectURL(this.filePreview);
        this.fileInput = file;
        this.filePreview = URL.createObjectURL(file);
      });

      this.Helper.successToast("JKR work order PDF added");
    },
    async getJkrWos() {
      if (!this.nodId) return;

      const [call, error] = await this.Helper.handlePromise(
        this.API.get(`nod-jkr-aks?nod_case=${this.nodId}&_sort=datetime:ASC`)
      );

      if (error) {
        this.triggerAlert("error", "Error getting jkr wo data");
      } else {
        this.jkrWos = call.data;

        // One editable draft per work order that has no working period yet,
        // but never on a frozen case — seeding one there would render live
        // date pickers and let a period be written after completion.
        // Whole-object assignment keeps Vue 2 reactivity without Vue.set.
        const drafts = {};

        if (!this.isFrozen) {
          this.jkrWos.forEach((wo) => {
            // Carry an in-progress edit across the refresh — rebuilding the
            // map wholesale used to discard dates the user had typed on
            // another card. Seed a blank draft only where there is neither a
            // working period nor an edit already open.
            if (this.dateDrafts[wo._id]) {
              drafts[wo._id] = this.dateDrafts[wo._id];
            } else if (!wo.start_date || !wo.end_date) {
              drafts[wo._id] = { start_date: null, end_date: null };
            }
          });
        }

        this.dateDrafts = drafts;
      }
    },
    editDates(wo) {
      this.dateDrafts = {
        ...this.dateDrafts,
        [wo._id]: { start_date: wo.start_date, end_date: wo.end_date },
      };
    },
    cancelEditDates(wo) {
      const drafts = { ...this.dateDrafts };
      delete drafts[wo._id];
      this.dateDrafts = drafts;
    },
    draftValid(woId) {
      const d = this.dateDrafts[woId];

      return d && d.start_date && d.end_date && d.start_date <= d.end_date;
    },
    async saveDates(wo) {
      if (this.isFrozen || !this.draftValid(wo._id)) return;

      const d = this.dateDrafts[wo._id];
      const clash = this.overlapsExisting(d.start_date, d.end_date, wo._id);

      if (clash) {
        return Swal.fire(
          "Dates overlap",
          `This period overlaps Work Order ${this.jkrWos.indexOf(clash) + 1} (${this.Helper.formatDate(clash.start_date)} — ${this.Helper.formatDate(clash.end_date)}). Work order periods cannot overlap.`,
          "warning"
        );
      }

      this.Helper.toggleLoadingSpinner(true);

      const [, error] = await this.Helper.handlePromise(
        this.API.put(`nod-jkr-aks/${wo._id}`, {
          start_date: d.start_date,
          end_date: d.end_date,
        })
      );

      this.Helper.toggleLoadingSpinner(false);

      if (error) {
        console.error(error);

        this.triggerAlert("error", "Error saving working period");
      } else {
        // Close this card's editor before refreshing, so the preserved-draft
        // logic in getJkrWos does not keep it open on the saved period.
        this.cancelEditDates(wo);

        await this.getJkrWos();

        this.Helper.successToast("Working period saved");

        // Refresh the parent nod so Site Diary picks up the new range.
        this.$emit("componentIsCompleted", wo._id);
      }
    },
  },
  async mounted() {
    this.Helper.toggleLoadingSpinner(true);

    await this.getJkrWos();

    this.Helper.toggleLoadingSpinner(false);

    if (this.extendSignal) this.consumeExtend();
  },
};
</script>
